import TocDetailSection from "./TocDetailSection.jsx";
import EffectIndicatorList from "./EffectIndicatorList.jsx";
import EmptyChip from "./TocEmptyChip.jsx";
import { getNode } from "../../helpers/tocHelpers/tocNavigatorChain";
import {
  stakeholderTypeLabels,
  primaryTargetGroupLabels,
  contextLabelLabels,
} from "../../helpers/dataHelpers/targetgroupCategories";
import {
  activityTypeLabels,
  activitySubtypeLabels,
  activitySubtypesByType,
  deliveryModeLabels,
  estimatedEffortLabels,
} from "../../helpers/dataHelpers/activityCategories";
import {
  measurementMethodLabels,
  measurementMethodOf,
  registrationTypeLabels,
  registrationTypeOf,
} from "../../helpers/dataHelpers/measurementMethods";

/**
 * Read-only detail view for ONE chain item in the TOC navigator's active card.
 * Mirrors what the inline NodeEditor sets (minus naam/beschrijving, which live
 * in the active-card header), as quiet label + text rows grouped in a few
 * titled blocks: Kenmerken, Koppelingen and — per step — Outputs/Indicatoren.
 *
 * @param {object} props
 * @param {{id: string, step: string, title: string, raw: object}} props.node - active navigator node
 * @param {object} props.model - navigator model (resolves linked node titles)
 * @param {Array<object>} props.outputs - live (non-deleted) Outputs docs, page-wide
 * @param {() => void} [props.onEdit] - opens the inline editor for this node; empty
 *   placeholders ("Nog niet ingesteld") become clickable shortcuts to it
 * @param {boolean} [props.mechanismsLoaded] - false while TocMechanisms is still
 *   loading: the 'Mechanismen' row then says so instead of "none linked"
 */
const NodeDetail = ({ node, model, outputs, onEdit, mechanismsLoaded = true }) => {
  if (!node) return null;
  let body = null;
  switch (node.step) {
    case "doel": body = <StakeholderNodeDetail item={node.raw} onEdit={onEdit} />; break;
    case "act": body = <ActivityNodeDetail item={node.raw} model={model} outputs={outputs} onEdit={onEdit} mechanismsLoaded={mechanismsLoaded} />; break;
    case "mech": body = <MechanismNodeDetail item={node.raw} model={model} onEdit={onEdit} />; break;
    case "eff": body = <EffectNodeDetail item={node.raw} model={model} onEdit={onEdit} mechanismsLoaded={mechanismsLoaded} />; break;
    default: return null;
  }
  return <div className="tocnav-grid">{body}</div>;
};

// ── shared bits ───────────────────────────────────────────────────────────────

/**
 * @typedef {object} DetailRow
 * @property {string} label - row label (left column)
 * @property {string[]} values - resolved display labels; joined with ", "
 * @property {string} [placeholder] - shown when `values` is empty
 * @property {boolean} [loading] - source still loading: show that, not "empty"
 */

/**
 * Label + text rows as a definition list. An empty row shows its placeholder,
 * which opens the editor when `onEmptyClick` is given.
 *
 * @param {object} props
 * @param {DetailRow[]} props.rows - one row per field
 * @param {() => void} [props.onEmptyClick] - forwarded to each empty placeholder
 */
const DetailList = ({ rows, onEmptyClick }) => (
  <dl className="tocnav-dl">
    {rows.map((row) => (
      <div className="tocnav-dl-row" key={row.label}>
        <dt className="tocnav-dl-label">{row.label}</dt>
        <dd className="tocnav-dl-value">
          {/* AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
          {row.loading
            ? <span className="tocnav-dl-loading">Laden…</span>
            : row.values.length > 0
              ? row.values.join(", ")
              : <EmptyChip onClick={onEmptyClick}>{row.placeholder || "Nog niet ingesteld"}</EmptyChip>}
        </dd>
      </div>
    ))}
  </dl>
);

/**
 * @param {string | undefined} key - stored taxonomy key
 * @param {Record<string, string>} labels - key → display label
 * @returns {string[]} zero or one label
 */
const one = (key, labels) => (key ? [labels[key] || key] : []);

/**
 * @param {object} model - navigator model
 * @param {string[] | undefined} ids - linked entity IDs
 * @returns {string[]} their titles, in stored order
 */
const titlesOf = (model, ids) => (ids || []).map((id) => getNode(model, id)?.title || "Naamloos");

/**
 * Titles of the mechanisms that link to this activity or effect. The link is
 * stored on the mechanism (`Activity[]` / `Effect[]`), not on the item itself.
 *
 * @param {object} model - navigator model
 * @param {"Activity" | "Effect"} field - which mechanism array holds the link
 * @param {string} id - the activity's or effect's ID
 * @returns {string[]}
 */
const linkedMechanismTitles = (model, field, id) =>
  (model?.byStep?.mech || [])
    .filter((m) => (m.raw[field] || []).includes(id))
    .map((m) => m.title || "Naamloos");

// ── Doelgroep ───────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} props.item - the Stakeholders doc of the active node
 * @param {() => void} [props.onEdit] - opens the inline editor (empty placeholders)
 */
const StakeholderNodeDetail = ({ item, onEdit }) => (
  <TocDetailSection label="Kenmerken">
    <DetailList
      onEmptyClick={onEdit}
      rows={[
        { label: "Type", values: one(item.stakeholderType, stakeholderTypeLabels) },
        { label: "Primaire doelgroep", values: (item.primaryTargetGroups || []).map((g) => primaryTargetGroupLabels[g] || g) },
        { label: "Contextkenmerken", values: (item.contextLabels || []).map((c) => contextLabelLabels[c] || c) },
      ]}
    />
  </TocDetailSection>
);

// ── Activiteit ──────────────────────────────────────────────────────────────────

/**
 * "Via formulier · Totaal over een periode", or just the method when the output
 * is not measured via the form (registration type only applies there, ADR-070).
 *
 * @param {object} output - the Outputs doc
 * @returns {string}
 */
const outputMeasurementSummary = (output) => {
  const method = measurementMethodOf(output);
  const methodLabel = measurementMethodLabels[method] || method;
  if (method !== "event_form") return methodLabel;
  return `${methodLabel} · ${registrationTypeLabels[registrationTypeOf(output)]}`;
};

/**
 * @param {object} props
 * @param {object} props.item - the Activities doc of the active node
 * @param {object} props.model - navigator model (resolves linked titles)
 * @param {Array<object>} props.outputs - live Outputs docs, page-wide
 * @param {() => void} [props.onEdit] - opens the inline editor (empty placeholders)
 * @param {boolean} props.mechanismsLoaded - false while TocMechanisms is loading
 */
const ActivityNodeDetail = ({ item, model, outputs, onEdit, mechanismsLoaded }) => {
  const loc = item.Location || {};
  const streetLine = [
    loc.Straatnaam,
    [loc.Huisnummer, loc.HuisnummerToevoeging].filter(Boolean).join(""),
  ].filter(Boolean).join(" ");
  const address = [streetLine, loc.Woonplaats].filter(Boolean).join(", ");

  const hasSubtypes = (activitySubtypesByType[item.activityType] || []).length > 0;
  const kenmerken = [
    { label: "Type", values: one(item.activityType, activityTypeLabels) },
    ...(hasSubtypes ? [{ label: "Subtype", values: one(item.activitySubtype, activitySubtypeLabels) }] : []),
    { label: "Uitvoering", values: one(item.deliveryMode, deliveryModeLabels) },
    { label: "Intensiteit", values: one(item.estimatedEffort, estimatedEffortLabels) },
  ];
  const koppelingen = [
    { label: "Doelgroepen", values: titlesOf(model, item.Targetgroup), placeholder: "Nog geen doelgroep gekoppeld" },
    { label: "Mechanismen", values: linkedMechanismTitles(model, "Activity", item.ID), placeholder: "Nog geen mechanisme gekoppeld", loading: !mechanismsLoaded },
    { label: "Locatie", values: address ? [address] : [], placeholder: "Geen locatie ingesteld" },
  ];

  const actOutputs = (outputs || []).filter((o) => o.ActivityID === item.ID);

  return (
    <>
      <TocDetailSection label="Kenmerken">
        <DetailList rows={kenmerken} onEmptyClick={onEdit} />
      </TocDetailSection>
      <TocDetailSection label="Koppelingen">
        <DetailList rows={koppelingen} onEmptyClick={onEdit} />
      </TocDetailSection>
      <TocDetailSection label="Outputs">
        {actOutputs.length === 0 ? (
          <EmptyChip onClick={onEdit}>Nog geen outputs</EmptyChip>
        ) : (
          <ul className="tocnav-output-list">
            {actOutputs.map((o) => (
              <li className="tocnav-output-item" key={o.ID}>
                <div className="tocnav-output-main">
                  <span className="tocnav-output-title">{o.Title || "Naamloos"}</span>
                  {o.Description && <p className="tocnav-output-desc">{o.Description}</p>}
                </div>
                <span className="tocnav-output-meta">{outputMeasurementSummary(o)}</span>
              </li>
            ))}
          </ul>
        )}
      </TocDetailSection>
    </>
  );
};

// ── Werkzaam mechanisme ─────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} props.item - the TocMechanisms doc of the active node
 * @param {object} props.model - navigator model (resolves linked titles)
 * @param {() => void} [props.onEdit] - opens the inline editor (empty placeholders)
 */
const MechanismNodeDetail = ({ item, model, onEdit }) => (
  <TocDetailSection label="Koppelingen">
    <DetailList
      onEmptyClick={onEdit}
      rows={[
        { label: "Activiteiten", values: titlesOf(model, item.Activity), placeholder: "Nog geen activiteit gekoppeld" },
        { label: "Effecten", values: titlesOf(model, item.Effect), placeholder: "Nog geen effect gekoppeld" },
      ]}
    />
  </TocDetailSection>
);

// ── Effect ──────────────────────────────────────────────────────────────────────

/**
 * @param {object} props
 * @param {object} props.item - the OutputEffects doc of the active node
 * @param {object} props.model - navigator model (resolves linked titles)
 * @param {() => void} [props.onEdit] - opens the inline editor (empty placeholders)
 * @param {boolean} props.mechanismsLoaded - false while TocMechanisms is loading
 */
const EffectNodeDetail = ({ item, model, onEdit, mechanismsLoaded }) => (
  <>
    <TocDetailSection label="Koppelingen">
      <DetailList
        onEmptyClick={onEdit}
        rows={[
          { label: "Activiteiten", values: titlesOf(model, item.Activity), placeholder: "Nog geen activiteit gekoppeld" },
          { label: "Mechanismen", values: linkedMechanismTitles(model, "Effect", item.ID), placeholder: "Nog geen mechanisme gekoppeld", loading: !mechanismsLoaded },
        ]}
      />
    </TocDetailSection>
    <TocDetailSection label="Indicatoren">
      {/* key: dezelfde component blijft staan bij het wisselen van effect, en
          de leesengine houdt de vorige lijst vast tot de nieuwe snapshot
          binnen is — remounten laat 'nog niet geladen' weer 'nog niet
          geladen' zijn in plaats van de indicatoren van het vorige effect. */}
      <EffectIndicatorList key={item.ID} effectId={item.ID} onEdit={onEdit} />
    </TocDetailSection>
  </>
);

export default NodeDetail;
